import { Alert, CircularProgress, Snackbar, TextField, Typography } from "@mui/material";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import { useTranslation } from "react-i18next";
import { useStyles } from "../../RCAMeetingSetting.styles";
import { SetStateAction, useMemo } from "react";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import { IMeetingItem } from "../../RCAMeetingTypesSettingInterfaces";
import { useDeleteMeetingActions } from "../../hooks/useDeleteMeetingActions";
import NoData from "components/NoData/NoData.view";

export const RCAMeetingDeletionModal = ({
    deleteMeeting,
    setDeleteMeeting,
    refetch,
}: {
    deleteMeeting: IMeetingItem;
    setDeleteMeeting: (value: SetStateAction<IMeetingItem | null>) => void;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        handleDeleteMeeting,
        setNewName,
        setReplacementMeeting,
        setSnackbarErrorMessage,
        setSnackbarMessage,
        nameTranslation,
        replacementMeeting,
        newName,
        snackbarErrorMessage,
        snackbarMessage,
        languageCode,
        loading,
        isUsed,
        error,
        isSubmitting,
    } = useDeleteMeetingActions(deleteMeeting, setDeleteMeeting, refetch);

    const modalInformation = useMemo(() => {
        if (loading) {
            return (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={24} color="info" />
                </div>
            );
        } else if (isUsed) {
            return (
                <>
                    <Typography style={{ marginTop: "16px" }}>
                        {t("rca.settings.meeting_settings.deletion_modal.reassignment_message")}
                    </Typography>
                    {replacementMeeting ? (
                        <RCAMeetingTypesDropdown
                            fullWidth
                            filterOutId={deleteMeeting?.id}
                            multiple={false}
                            value={replacementMeeting}
                            onChange={(value) => {
                                setNewName("");
                                setReplacementMeeting(value);
                            }}
                        />
                    ) : (
                        <RCAMeetingTypesDropdown
                            fullWidth
                            filterOutId={deleteMeeting?.id}
                            multiple={false}
                            onChange={(value) => {
                                setNewName("");
                                setReplacementMeeting(value);
                            }}
                        />
                    )}
                    <Typography style={{ marginTop: "16px" }}>
                        {t("rca.settings.meeting_settings.deletion_modal.new_name_message")}
                    </Typography>
                    <TextField
                        fullWidth
                        id="meeting-type-name-input"
                        name="name"
                        type="text"
                        variant="outlined"
                        className={classes.textFieldModal}
                        label={`${t("rca.settings.meeting_settings.textfield_label")} (${languageCode})`}
                        value={newName}
                        onChange={(event) => {
                            setReplacementMeeting(undefined);
                            setNewName(event.target.value);
                        }}
                    />
                </>
            );
        } else if (error) {
            return <NoData title={t("rca.settings.meeting_settings.deletion_modal.error")} />;
        } else if (isUsed === false) {
            return undefined;
        }
    }, [
        isUsed,
        t,
        error,
        loading,
        classes,
        deleteMeeting,
        languageCode,
        newName,
        replacementMeeting,
        setNewName,
        setReplacementMeeting,
    ]);

    const warningMessage = useMemo(
        () =>
            !loading && !error
                ? isUsed
                    ? t("rca.settings.meeting_settings.deletion_modal.warning", {
                          name: nameTranslation,
                      })
                    : t("rca.settings.meeting_settings.deletion_modal.simple_warning", {
                          name: nameTranslation,
                      })
                : "",
        [loading, error, isUsed, nameTranslation, t],
    );

    return (
        <>
            <RCAConfirmDeleteModal
                open={!!deleteMeeting}
                onClose={() => setDeleteMeeting(null)}
                bodyClassname={classes.contentCardContent}
                onDeleteItem={async () => {
                    await handleDeleteMeeting();
                }}
                title={t("rca.settings.meeting_settings.deletion_modal.title")}
                warning={warningMessage}
                confirm={t("rca.settings.meeting_settings.deletion_modal.confirm")}
                children={modalInformation}
                canDelete={!!loading || !!error || isSubmitting}
            />
            <Snackbar
                open={!!snackbarMessage || !!snackbarErrorMessage}
                onClose={() => {
                    setSnackbarMessage("");
                    setSnackbarErrorMessage("");
                }}
                autoHideDuration={6000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={snackbarErrorMessage ? "error" : "success"} sx={{ width: "100%" }}>
                    {snackbarMessage || snackbarErrorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};
